import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import {
  Activity, AlertTriangle, Shield, TrendingUp, TrendingDown, Minus,
  BarChart3, Target, Eye, RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";

const STATUS_CONFIG = {
  good: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-700", stroke: "#059669", fill: "rgba(5,150,105,0.12)" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500", badge: "bg-amber-100 text-amber-700", stroke: "#d97706", fill: "rgba(217,119,6,0.12)" },
  danger: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500", badge: "bg-red-100 text-red-700", stroke: "#dc2626", fill: "rgba(220,38,38,0.12)" },
};

const SEVERITY_CONFIG = {
  critical: { badge: "bg-red-100 text-red-800 border-red-200" },
  high: { badge: "bg-orange-100 text-orange-800 border-orange-200" },
  medium: { badge: "bg-amber-100 text-amber-800 border-amber-200" },
  low: { badge: "bg-slate-100 text-slate-700 border-slate-200" },
};

const TrendIcon = ({ trend }) => {
  if (trend === "up") return <TrendingUp className="w-3 h-3 text-emerald-500" />;
  if (trend === "down") return <TrendingDown className="w-3 h-3 text-red-500" />;
  return <Minus className="w-3 h-3 text-slate-400" />;
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-slate-800 text-white px-3 py-2 rounded-lg shadow-lg text-xs">
        <p className="font-semibold">{d.label}</p>
        <p className="text-slate-300">Score: <span className="font-bold text-white">{d.health_score}</span></p>
      </div>
    );
  }
  return null;
};

export default function BCMHealthDashboard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const [summaryRes, trendRes] = await Promise.all([
        api.get("/bcm-health/summary"),
        api.get("/bcm-health/trend"),
      ]);
      if (summaryRes.data?.success) setData(summaryRes.data.data);
      if (trendRes.data?.success) setTrend(trendRes.data.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm" data-testid="bcm-health-loading">
        <div className="flex items-center justify-center gap-3">
          <div className="w-5 h-5 border-2 border-sky-700 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading BCM Health Dashboard...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const notScored = data.health_score === null || data.health_score === undefined;
  const NEUTRAL = { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500", dot: "bg-slate-300", badge: "bg-slate-100 text-slate-600", stroke: "#94a3b8", fill: "rgba(148,163,184,0.12)" };
  const sc = notScored ? NEUTRAL : (STATUS_CONFIG[data.health_status] || STATUS_CONFIG.warning);

  // Prepare sparkline data
  const sparklineData = (trend?.snapshots || []).map((s) => ({
    ...s,
    label: new Date(s.date + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  }));

  const trendLabel = trend?.trend_direction === "improving"
    ? `+${trend.change} pts` : trend?.trend_direction === "declining"
    ? `${trend.change} pts` : "Stable";

  const trendColor = trend?.trend_direction === "improving"
    ? "text-emerald-600" : trend?.trend_direction === "declining"
    ? "text-red-600" : "text-slate-500";

  const trendBg = trend?.trend_direction === "improving"
    ? "bg-emerald-50" : trend?.trend_direction === "declining"
    ? "bg-red-50" : "bg-slate-50";

  return (
    <div className="space-y-4" data-testid="bcm-health-dashboard">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center shadow-sm">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              BCM Health Dashboard
            </h2>
            <p className="text-[10px] text-slate-400">Executive view — aggregated from {data.reports_analyzed} reports</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/reports">
            <button className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-sky-50 hover:border-sky-200 hover:text-sky-700 transition-all flex items-center gap-1.5" data-testid="bcm-health-view-reports">
              <Eye className="w-3.5 h-3.5" /> View Reports
            </button>
          </Link>
          <button
            onClick={() => fetchAll(true)}
            disabled={refreshing}
            className="p-1.5 border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-50 transition-colors"
            data-testid="bcm-health-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Health Score + Trend Sparkline + Summary Stats Row */}
      <div className="grid grid-cols-[200px_1fr] gap-4">
        {/* Left: Health Score Ring + Trend */}
        <div className="space-y-3">
          {/* Health Score Card */}
          <div className={`${sc.bg} border ${sc.border} rounded-xl p-5 flex flex-col items-center justify-center`} data-testid="bcm-health-score-card">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Overall Health</p>
            <div className={`w-[100px] h-[100px] rounded-full border-[8px] ${sc.text.replace("text-", "border-")} flex items-center justify-center`}>
              <span className={`${notScored ? "text-[11px] font-semibold text-center leading-tight px-1" : "text-3xl font-extrabold"} ${sc.text}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {notScored ? "Not yet scored" : data.health_score}
              </span>
            </div>
            <Badge className={`mt-3 text-[10px] font-semibold ${sc.badge}`} data-testid="bcm-health-status-badge">
              {notScored ? "Not yet scored" : (data.health_status === "good" ? "Healthy" : data.health_status === "warning" ? "Needs Attention" : "At Risk")}
            </Badge>
            <div className="grid grid-cols-3 gap-2 mt-4 w-full text-center">
              <div>
                <p className={`${data.total_kpis == null ? "text-[9px] font-semibold text-slate-400 leading-tight" : "text-lg font-bold text-slate-800"}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.total_kpis == null ? "Not yet scored" : data.total_kpis}</p>
                <p className="text-[9px] text-slate-400 font-medium">KPIs</p>
              </div>
              <div>
                <p className={`${data.total_kris == null ? "text-[9px] font-semibold text-slate-400 leading-tight" : "text-lg font-bold text-slate-800"}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.total_kris == null ? "Not yet scored" : data.total_kris}</p>
                <p className="text-[9px] text-slate-400 font-medium">KRIs</p>
              </div>
              <div>
                <p className={`${data.total_attention_items == null ? "text-[9px] font-semibold text-slate-400 leading-tight" : "text-lg font-bold text-slate-800"}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.total_attention_items == null ? "Not yet scored" : data.total_attention_items}</p>
                <p className="text-[9px] text-slate-400 font-medium">Alerts</p>
              </div>
            </div>
          </div>

          {/* Trend Sparkline Card */}
          {notScored ? (
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="bcm-health-trend-card">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">12-Week Trend</p>
              <div className="h-[70px] flex items-center justify-center" data-testid="bcm-health-sparkline">
                <span className="text-xs font-semibold text-slate-400">Not yet scored</span>
              </div>
            </div>
          ) : sparklineData.length >= 2 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="bcm-health-trend-card">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">12-Week Trend</p>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${trendBg} ${trendColor}`} data-testid="bcm-health-trend-badge">
                  {trend?.trend_direction === "improving" && <TrendingUp className="w-3 h-3 inline mr-1" />}
                  {trend?.trend_direction === "declining" && <TrendingDown className="w-3 h-3 inline mr-1" />}
                  {trendLabel}
                </span>
              </div>
              <div className="h-[70px]" data-testid="bcm-health-sparkline">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparklineData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                    <defs>
                      <linearGradient id="healthGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={sc.stroke} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={sc.stroke} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" hide />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="health_score"
                      stroke={sc.stroke}
                      strokeWidth={2}
                      fill="url(#healthGradient)"
                      dot={false}
                      activeDot={{ r: 4, fill: sc.stroke, strokeWidth: 0 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between mt-1 text-[9px] text-slate-400">
                <span>{sparklineData[0]?.label}</span>
                <span>{sparklineData[sparklineData.length - 1]?.label}</span>
              </div>
            </div>
          )}
        </div>

        {/* Right: Domain Scores Grid */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="bcm-domain-scores">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-sky-700" />
              <h3 className="text-xs font-bold text-slate-700">Domain Health Scores</h3>
            </div>
            <span className="text-[10px] text-slate-400">{data.domain_scores?.length || 0} domains</span>
          </div>
          <div className="p-3 grid grid-cols-3 gap-2">
            {(data.domain_scores || []).map((d) => {
              const dNull = d.score === null || d.score === undefined;
              const dsc = dNull ? NEUTRAL : (STATUS_CONFIG[d.status] || STATUS_CONFIG.warning);
              return (
                <div key={d.report_id} className={`p-2.5 rounded-lg border ${dsc.border} ${dsc.bg}`} data-testid={`domain-${d.report_id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-semibold text-slate-700 truncate pr-2" title={d.domain}>
                      {d.domain.length > 28 ? d.domain.slice(0, 28) + "..." : d.domain}
                    </p>
                    <span className={`${dNull ? "text-[10px] font-semibold" : "text-sm font-bold"} ${dsc.text}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{dNull ? "Not yet scored" : d.score}</span>
                  </div>
                  <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${dsc.dot} transition-all duration-500`} style={{ width: `${dNull ? 0 : d.score}%` }} />
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-[9px] text-slate-500">
                    {dNull || d.kpi_count == null ? (
                      <span>Not yet scored</span>
                    ) : (
                      <>
                        <span>{d.kpi_count} KPIs</span>
                        <span>{d.kri_count} KRIs</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top KPIs + KRIs + Attention Items Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Top KPIs */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="bcm-top-kpis">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Target className="w-4 h-4 text-sky-700" />
            <h3 className="text-xs font-bold text-slate-700">Priority KPIs</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
            {(data.top_kpis || []).slice(0, 6).map((kpi, i) => {
              const ksc = STATUS_CONFIG[kpi.status] || STATUS_CONFIG.warning;
              return (
                <div key={i} className="px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`kpi-item-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ksc.dot}`} />
                      <span className="text-xs font-medium text-slate-700 truncate">{kpi.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      <span className={`text-xs font-bold ${ksc.text}`}>{kpi.value}</span>
                      <TrendIcon trend={kpi.trend} />
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5 pl-4">{kpi.report}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top KRIs */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="bcm-top-kris">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h3 className="text-xs font-bold text-slate-700">Key Risk Indicators</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
            {(data.top_kris || []).slice(0, 6).map((kri, i) => {
              const sev = SEVERITY_CONFIG[kri.severity] || SEVERITY_CONFIG.medium;
              return (
                <div key={i} className="px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`kri-item-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-700 truncate flex-1">{kri.name}</span>
                    <Badge className={`text-[9px] ml-2 border ${sev.badge}`}>{kri.severity}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-[10px] text-slate-400">{kri.report}</p>
                    <span className="text-[10px] font-semibold text-slate-600">Val: {kri.value} / Thr: {kri.threshold}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Critical Attention Items */}
        <div className="bg-white border border-red-200 rounded-xl shadow-sm overflow-hidden" data-testid="bcm-critical-attention">
          <div className="px-4 py-3 border-b border-red-100 bg-red-50/50 flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-600" />
            <h3 className="text-xs font-bold text-slate-700">Critical Attention</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
            {(data.critical_attention || []).slice(0, 5).map((item, i) => {
              const pri = SEVERITY_CONFIG[item.priority] || SEVERITY_CONFIG.medium;
              return (
                <div key={i} className="px-4 py-2.5 hover:bg-red-50/30 transition-colors" data-testid={`attention-item-${i}`}>
                  <div className="flex items-start gap-2">
                    <Badge className={`text-[9px] border flex-shrink-0 mt-0.5 ${pri.badge}`}>{item.priority}</Badge>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-700">{item.title}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-2">{item.recommendation || item.detail}</p>
                      <p className="text-[9px] text-slate-400 mt-1 italic">{item.report}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
